import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import {
  ClientFilterForm,
  ClientFilterFormRef,
  ClientFilterFormValues,
} from "@components/Forms/ClientFilterForm/ClientFilterForm";
import { ClientsTable } from "@components/Tables";
import { ClientsTableConfig } from "@components/Tables/ClientsTable/ClientsTableConfig";
import { ClientsTableColumnCustomization } from "@components/Tables/ClientsTable/UI/ClientsTableColumnCustomization";
import {
  DEFAULT_CLIENT_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_TABLE_COLUMN_SETTINGS,
} from "@components/Tables/ClientsTable/UI/ClientsTableColumns";
import { useClientTableExport } from "@components/Tables/ClientsTable/utils/useClientTableExport";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";
import { Text } from "@design-system/Text/Text";

import { PortalHeaderActions } from "@layouts/PortalPage/PortalHeaderActions";

import { routes } from "@pages/routes";

const getDefaultFilterValues = (): ClientFilterFormValues => ({
  nameSearch: "",
  domainSearch: "",
  emailSearch: "",
});

const areFiltersActive = (filterValues: ClientFilterFormValues): boolean => {
  return Boolean(
    (filterValues.nameSearch && filterValues.nameSearch.trim()) ||
      (filterValues.domainSearch && filterValues.domainSearch.trim()) ||
      (filterValues.emailSearch && filterValues.emailSearch.trim()),
  );
};

type Props = {
  initialClientTablePageIndex?: number;
};

export const ClientsCard = ({ initialClientTablePageIndex = 0 }: Props) => {
  const navigate = useNavigate();
  const [filterValues, setFilterValues] = useState<ClientFilterFormValues>(
    getDefaultFilterValues(),
  );
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnCustomizationOpen, setIsColumnCustomizationOpen] =
    useState(false);

  const filterFormRef = useRef<ClientFilterFormRef>(null);

  const filtersActive = useMemo(() => {
    return areFiltersActive(filterValues);
  }, [filterValues]);

  const exportFilters = useMemo(
    () => ({
      name_Icontains: filterValues.nameSearch || undefined,
      domain_Icontains: filterValues.domainSearch || undefined,
      email_Icontains: filterValues.emailSearch || undefined,
    }),
    [
      filterValues.nameSearch,
      filterValues.domainSearch,
      filterValues.emailSearch,
    ],
  );

  const { exportData, isExporting } = useClientTableExport({
    tableId: "clients",
    filters: exportFilters,
  });

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilterValues(getDefaultFilterValues());
  }, []);

  const handleFilterChange = useCallback(
    (newValues: ClientFilterFormValues) => {
      // Called when form is submitted (Enter key)
      setFilterValues(newValues);
      setIsFilterPanelOpen(false);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    // Get current values from the form and apply them
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
      setIsFilterPanelOpen(false);
    }
  }, []);

  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnCustomizationOpen(true);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setIsColumnCustomizationOpen(false);
  }, []);

  const handleExportData = useCallback(async () => {
    try {
      await exportData();
      toast.success("Tenants exported successfully");
    } catch (error) {
      toast.error("Unable to export tenants");
      console.error(error);
    }
  }, [exportData]);

  const handleCreateClientClick = useCallback(() => {
    navigate(routes.portal.clients.create.$path(), { viewTransition: true });
  }, [navigate]);

  const isFiltersActive = filtersActive;

  return (
    <TableColumnSettingsProvider
      tableId={ClientsTableConfig.tableId}
      defaultColumnSettings={DEFAULT_CLIENT_TABLE_COLUMN_SETTINGS}
      defaultColumnOrder={DEFAULT_CLIENT_TABLE_COLUMN_ORDER}
    >
      <PortalHeaderActions>
        <Box direction="row" gap={8} align="center">
          {isFiltersActive && (
            <Text size={12} color={"textSecondary"}>
              Filters are applied
            </Text>
          )}
          <IconButton
            size="sm"
            icon="faFilter"
            onClick={handleFilterClick}
            outline={"sm"}
            showBubble={isFiltersActive}
            tooltip="Show filters"
          />
          <IconButton
            size="sm"
            icon="faTableColumns"
            onClick={handleColumnSettingsClick}
            outline={"sm"}
            tooltip="Show/Hide columns"
          />
          <IconButton
            size="sm"
            icon={isExporting ? "faArrowsSpin" : "faFileCsv"}
            onClick={handleExportData}
            outline={"sm"}
            tooltip={isExporting ? "Exporting..." : "Export table data to CSV"}
            disabled={isExporting}
          />
          <Button onClick={handleCreateClientClick} size="sm" icon="faPlus">
            Create tenant
          </Button>
        </Box>
      </PortalHeaderActions>
      <ClientsTable
        initialPageIndex={initialClientTablePageIndex}
        nameSearch={filterValues.nameSearch}
        domainSearch={filterValues.domainSearch}
        emailSearch={filterValues.emailSearch}
      />

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Tenant Filters"
      >
        <ClientFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
        />
      </SlideInPanel>

      <ClientsTableColumnCustomization
        isOpen={isColumnCustomizationOpen}
        onClose={handleCloseColumnSettings}
      />
    </TableColumnSettingsProvider>
  );
};
